package com.trevorwiebe.timesheet.admin.ownerOperator.presentation

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material3.Card
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FilterChip
import androidx.compose.material3.FilterChipDefaults
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.formatPercent
import com.trevorwiebe.timesheet.core.domain.toPercentOrNull
import com.trevorwiebe.timesheet.core.domain.model.OwnerOperator
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.BackIcon
import com.trevorwiebe.timesheet.core.presentation.common.DialogButtonRow
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.core.presentation.common.TimesheetTextField
import com.trevorwiebe.timesheet.core.presentation.common.responsiveDialogWidth
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun OwnerOperatorsScreen(
    viewModel: OwnerOperatorsViewModel = koinViewModel(),
    onBack: () -> Unit
) {
    val state by viewModel.state.collectAsState()

    Scaffold(
        topBar = {
            TopBar(
                title = "Owner Operators",
                backIcon = { BackIcon() },
                onBack = onBack
            )
        },
        floatingActionButton = {
            FloatingActionButton(
                onClick = { viewModel.onEvent(OwnerOperatorsEvent.OnAddOwnerOperatorClick) },
                containerColor = MaterialTheme.colorScheme.tertiary,
                contentColor = MaterialTheme.colorScheme.onTertiary
            ) {
                Icon(Icons.Default.Add, contentDescription = "Add owner operator")
            }
        }
    ) { paddingValues ->
        ResponsiveContent(modifier = Modifier.padding(paddingValues)) {
            Column(
                modifier = Modifier.fillMaxSize()
            ) {
                OwnerOperatorsFilterChipRow(
                    activeFilters = state.activeFilters,
                    onFilterToggled = { viewModel.onEvent(OwnerOperatorsEvent.OnFilterToggled(it)) }
                )
                HorizontalDivider()
                if (state.isLoading) {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator(color = MaterialTheme.colorScheme.tertiary)
                    }
                } else {
                    LazyColumn(modifier = Modifier.fillMaxSize()) {
                        items(state.displayedOwnerOperators, key = { it.id }) { ownerOperator ->
                            OwnerOperatorRow(
                                ownerOperator = ownerOperator,
                                onClick = {
                                    viewModel.onEvent(
                                        OwnerOperatorsEvent.OnEditOwnerOperatorClick(ownerOperator)
                                    )
                                }
                            )
                        }
                    }
                }
            }

            if (state.showAddEditDialog) {
                AddEditOwnerOperatorDialog(
                    editing = state.editingOwnerOperator,
                    onDismiss = { viewModel.onEvent(OwnerOperatorsEvent.OnDismissDialog) },
                    onSave = { contactName, companyName, address, phone, email, notes, basePayPercent, active ->
                        viewModel.onEvent(
                            OwnerOperatorsEvent.OnSaveOwnerOperator(
                                contactName, companyName, address, phone, email, notes, basePayPercent, active
                            )
                        )
                    },
                    onDelete = { viewModel.onEvent(OwnerOperatorsEvent.OnDeleteOwnerOperator) },
                    onRestore = { viewModel.onEvent(OwnerOperatorsEvent.OnRestoreOwnerOperator) }
                )
            }

            NativeDialog(
                visible = state.error != null,
                title = "Something went wrong",
                message = state.error ?: "",
                confirmText = "OK",
                onConfirm = { viewModel.onEvent(OwnerOperatorsEvent.OnErrorShown) },
                onDismiss = { viewModel.onEvent(OwnerOperatorsEvent.OnErrorShown) }
            )
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun OwnerOperatorsFilterChipRow(
    activeFilters: Set<OwnerOperatorFilter>,
    onFilterToggled: (OwnerOperatorFilter) -> Unit
) {
    FlowRow(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp, vertical = 4.dp),
        horizontalArrangement = Arrangement.spacedBy(6.dp),
        verticalArrangement = Arrangement.spacedBy((-8).dp)
    ) {
        Text(
            text = "Filter",
            fontSize = 14.sp,
            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f),
            modifier = Modifier.align(Alignment.CenterVertically).padding(end = 4.dp)
        )
        OwnerOperatorFilter.entries.forEach { filter ->
            val selected = filter in activeFilters
            FilterChip(
                selected = selected,
                onClick = { onFilterToggled(filter) },
                label = { Text(filter.name.lowercase().replaceFirstChar { it.uppercase() }) },
                border = FilterChipDefaults.filterChipBorder(
                    enabled = true,
                    borderColor = MaterialTheme.colorScheme.tertiary,
                    selected = selected
                ),
                colors = FilterChipDefaults.filterChipColors(
                    containerColor = MaterialTheme.colorScheme.background,
                    selectedContainerColor = MaterialTheme.colorScheme.tertiary,
                    selectedLabelColor = MaterialTheme.colorScheme.onTertiary
                )
            )
        }
    }
}

@Composable
private fun OwnerOperatorRow(
    ownerOperator: OwnerOperator,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(start = 16.dp, end = 16.dp, top = 12.dp, bottom = 12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = ownerOperator.contactName.ifEmpty { "(no name)" },
                    fontSize = 16.sp,
                    fontWeight = FontWeight.SemiBold,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                val contactLine = listOf(
                    ownerOperator.companyName,
                    ownerOperator.email,
                    ownerOperator.basePayPercent?.let { "${it.formatPercent()}% base pay" } ?: "Base pay not set"
                ).filter { it.isNotBlank() }.joinToString(" · ")
                if (contactLine.isNotEmpty()) {
                    Text(
                        text = contactLine,
                        fontSize = 14.sp,
                        color = MaterialTheme.colorScheme.secondary,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }
            when {
                ownerOperator.deleted -> Text(
                    text = "Deleted",
                    color = MaterialTheme.colorScheme.error,
                    fontSize = 14.sp,
                    maxLines = 1
                )
                !ownerOperator.active -> Text(
                    text = "Inactive",
                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f),
                    fontSize = 14.sp,
                    maxLines = 1
                )
            }
        }
        HorizontalDivider()
    }
}

@Composable
private fun AddEditOwnerOperatorDialog(
    editing: OwnerOperator?,
    onDismiss: () -> Unit,
    onSave: (
        contactName: String,
        companyName: String,
        address: String,
        phone: String,
        email: String,
        notes: String,
        basePayPercent: Double?,
        active: Boolean
    ) -> Unit,
    onDelete: () -> Unit,
    onRestore: () -> Unit
) {
    var contactName by remember { mutableStateOf(editing?.contactName ?: "") }
    var companyName by remember { mutableStateOf(editing?.companyName ?: "") }
    var address by remember { mutableStateOf(editing?.address ?: "") }
    var phone by remember { mutableStateOf(editing?.phone ?: "") }
    var email by remember { mutableStateOf(editing?.email ?: "") }
    var notes by remember { mutableStateOf(editing?.notes ?: "") }
    var basePayText by remember { mutableStateOf(editing?.basePayPercent?.formatPercent() ?: "") }
    var active by remember { mutableStateOf(editing?.active ?: true) }
    var showDeleteConfirm by remember { mutableStateOf(false) }
    var validationError by remember { mutableStateOf<String?>(null) }

    NativeDialog(
        visible = showDeleteConfirm,
        title = "Delete Owner Operator",
        message = "Are you sure you want to delete this owner operator? It can be restored, and existing records will keep their reference.",
        confirmText = "Delete",
        onConfirm = onDelete,
        dismissText = "Cancel",
        onDismiss = { showDeleteConfirm = false }
    )

    Dialog(onDismissRequest = onDismiss) {
        Card(
            modifier = Modifier.responsiveDialogWidth(),
            shape = RoundedCornerShape(16.dp)
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .imePadding()
                    .verticalScroll(rememberScrollState())
                    .padding(16.dp)
            ) {
                Text(
                    text = if (editing == null) "Add Owner Operator" else "Edit Owner Operator",
                    fontSize = 20.sp,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.align(Alignment.CenterHorizontally)
                )
                Spacer(modifier = Modifier.height(16.dp))

                TimesheetTextField(
                    text = companyName,
                    label = "Company Name",
                    singleLine = true,
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words),
                    onTextChange = { companyName = it }
                )
                Spacer(modifier = Modifier.height(8.dp))
                TimesheetTextField(
                    text = contactName,
                    label = "Contact Name",
                    singleLine = true,
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words),
                    onTextChange = { contactName = it }
                )
                Spacer(modifier = Modifier.height(8.dp))
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    TimesheetTextField(
                        modifier = Modifier.weight(1f),
                        text = email,
                        label = "Email",
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                        onTextChange = { email = it }
                    )
                    TimesheetTextField(
                        modifier = Modifier.weight(1f),
                        text = phone,
                        label = "Phone",
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                        onTextChange = { phone = it }
                    )
                }
                Spacer(modifier = Modifier.height(8.dp))
                TimesheetTextField(
                    text = address,
                    label = "Address",
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words),
                    onTextChange = { address = it }
                )

                SectionLabel("Settlement pay")
                TimesheetTextField(
                    text = basePayText,
                    label = "Base pay (%)",
                    singleLine = true,
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                    onTextChange = { basePayText = it }
                )
                Text(
                    text = "Share of each load's base rate. Fuel surcharge and other charges are paid " +
                        "at 100% unless changed on a settlement.",
                    fontSize = 12.sp,
                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
                    modifier = Modifier.padding(top = 4.dp, start = 4.dp)
                )

                SectionLabel("Other")
                TimesheetTextField(
                    text = notes,
                    label = "Notes",
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences),
                    onTextChange = { notes = it }
                )
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(vertical = 4.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(text = "Active", fontSize = 16.sp)
                    Switch(
                        checked = active,
                        onCheckedChange = { active = it },
                        colors = SwitchDefaults.colors(
                            checkedThumbColor = MaterialTheme.colorScheme.tertiary,
                            checkedTrackColor = MaterialTheme.colorScheme.tertiary.copy(alpha = 0.5f)
                        )
                    )
                }

                validationError?.let { error ->
                    Text(
                        text = error,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall,
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(vertical = 8.dp)
                    )
                }

                val isDeleted = editing?.deleted == true
                DialogButtonRow(
                    confirmButtonText = "Save",
                    confirmEnabled = !isDeleted,
                    onConfirmClick = {
                        val basePay = basePayText.toPercentOrNull()
                        validationError = when {
                            contactName.isBlank() -> "Please enter Contact Name before saving."
                            phone.isNotBlank() && !Util.isValidUsPhone(phone.trim()) ->
                                "Please enter a valid US phone number."
                            email.isNotBlank() && !Util.isValidEmail(email.trim()) ->
                                "Please enter a valid email address."
                            basePayText.isNotBlank() && basePay == null ->
                                "Base pay must be a percentage from 0 to 100."
                            else -> null
                        }
                        if (validationError == null) {
                            onSave(
                                contactName.trim(),
                                companyName.trim(),
                                address.trim(),
                                phone.trim(),
                                email.trim(),
                                notes.trim(),
                                basePay,
                                active
                            )
                        }
                    },
                    dismissButtonText = "Cancel",
                    onDismissClick = onDismiss,
                    neutralButtonText = when {
                        editing == null -> null
                        isDeleted -> "Restore"
                        else -> "Delete"
                    },
                    onNeutralClick = if (isDeleted) onRestore else { { showDeleteConfirm = true } }
                )
            }
        }
    }
}

@Composable
private fun SectionLabel(text: String) {
    Text(
        text = text.uppercase(),
        fontSize = 12.sp,
        fontWeight = FontWeight.Medium,
        color = MaterialTheme.colorScheme.tertiary,
        modifier = Modifier.padding(top = 20.dp, bottom = 8.dp, start = 4.dp)
    )
}
